import { Button } from '@progress/kendo-react-buttons';
import { NumericTextBox, TextBox } from '@progress/kendo-react-inputs';
import { useRef, useState } from 'react';
import { AddOutline, TrashOutline } from '../../../../../../icons/icons';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/store-hooks';
import { SettingsNavTitle, useSettingsPageWidth } from '../../../context/SettingsNavContext';
import { AircraftTypeRecord } from '@fe/flight-layer';
import { produce } from 'immer';
import { setSetting } from '../../../../../state/slices/settings-slice';
import { useBlocker } from 'react-router';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';

export default function SettingsAircraftTypeParameters() {
    useSettingsPageWidth('700px');
    const [edited, setEdited] = useState(false);
    const idSource = useRef(0);
    const defaultParams = useAppSelector((x) => x.settings.defaultAircraftParameters);
    const customParams = useAppSelector((x) => x.settings.aircraftParameters);
    const [duplicateError, setDuplicateError] = useState(false);
    const dispatch = useAppDispatch();
    const blocker = useBlocker(edited);

    const [editCopyDefault, setEditCopyDefault] = useState(
        () =>
            defaultParams ??
            ({
                acType: null,
                speed: null,
                fuelBurningRate: null,
                fuelReserve: null
            } satisfies AircraftTypeRecord)
    );
    const [editCopyCustom, setEditCopyCustom] = useState(() => customParams.map((x) => ({ ...x, id: idSource.current++ })));

    const handleSave = () => {
        setDuplicateError(false);
        const filtered = editCopyCustom.filter((x) => x.acType);
        setEditCopyCustom(filtered);

        const hasDuplicates = filtered.length !== new Set(filtered.map((x) => x.acType)).size;
        if (hasDuplicates) {
            setDuplicateError(true);
            return false;
        }

        dispatch(
            setSetting(
                'aircraftParameters',
                // Remap it to remove ID fields
                filtered.map((x) => ({
                    acType: x.acType,
                    speed: x.speed,
                    fuelBurningRate: x.fuelBurningRate,
                    fuelReserve: x.fuelReserve
                }))
            )
        );
        dispatch(setSetting('defaultAircraftParameters', editCopyDefault));

        setEdited(false);
        setDuplicateError(false);
        return true;
    };

    const addCustom = () => {
        setEdited(true);
        setEditCopyCustom((p) => [
            ...p,
            {
                id: idSource.current++,
                acType: '',
                speed: null,
                fuelBurningRate: null,
                fuelReserve: null
            }
        ]);
    };

    const removeCustom = (id: number) => {
        setEdited(true);
        setEditCopyCustom((p) => p.filter((x) => x.id !== id));
    };

    const editDefaultValue = <T extends keyof AircraftTypeRecord>(type: T, value: AircraftTypeRecord[T]) => {
        setEdited(true);

        setEditCopyDefault((p) => ({
            ...p,
            [type]: value
        }));
    };

    const editCustomValue = <T extends keyof AircraftTypeRecord>(id: number, type: T, value: AircraftTypeRecord[T]) => {
        setEdited(true);

        setEditCopyCustom(
            produce(editCopyCustom, (draft) => {
                const item = draft.find((x) => x.id === id);
                if (item) {
                    item[type] = value as any;
                }
            })
        );
    };

    return (
        <div>
            {blocker.state === 'blocked' && (
                <Dialog title="Aircraft parameters" modal={true} onClose={() => blocker.reset()}>
                    <div>Do you want to save pending changes?</div>
                    <DialogActionsBar layout="stretched">
                        <Button
                            themeColor={'primary'}
                            onClick={() => {
                                if (handleSave()) {
                                    blocker.proceed();
                                } else {
                                    blocker.reset();
                                }
                            }}>
                            Yes
                        </Button>
                        <Button fillMode={'outline'} onClick={() => blocker.proceed()}>
                            No
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}
            <SettingsNavTitle>
                <div className="d-flex justify-content-between align-items-center">
                    <div>Aircraft types parameters</div>
                    <Button disabled={!edited} fillMode={'outline'} onClick={handleSave}>
                        Save
                    </Button>
                </div>
            </SettingsNavTitle>

            <div className="k-card p-3">
                {duplicateError && (
                    <div className="text-error secondary-text pb-2">
                        Duplicate aircraft types detected. Please ensure all custom types are unique.
                    </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) max-content', gap: '0.5rem', alignItems: 'end' }}>
                    <div>Aircraft type</div>
                    <div>Speed (kts)</div>
                    <div>Fuel burn</div>
                    <div>Fuel reserve</div>
                    <div></div>

                    <div style={{ alignSelf: 'center' }}>Default</div>
                    <NumericTextBox
                        min={0}
                        placeholder="Speed"
                        value={editCopyDefault.speed}
                        format={'0 kts'}
                        onChange={(e) => editDefaultValue('speed', e.value)}
                    />
                    <NumericTextBox
                        min={0}
                        placeholder="Fuel burn"
                        format={'0 lb/h'}
                        value={editCopyDefault.fuelBurningRate}
                        onChange={(e) => editDefaultValue('fuelBurningRate', e.value)}
                    />
                    <NumericTextBox
                        min={0}
                        placeholder="Fuel reserve"
                        format={'0 lb'}
                        value={editCopyDefault.fuelReserve}
                        onChange={(e) => editDefaultValue('fuelReserve', e.value)}
                    />

                    <div className="d-flex justify-content-between align-items-end" style={{ gridColumn: '1/6' }}>
                        Custom:
                        <Button svgIcon={AddOutline} themeColor={'primary'} onClick={addCustom}></Button>
                    </div>

                    {editCopyCustom.map((x) => (
                        <div key={x.id} style={{ display: 'contents' }}>
                            <TextBox
                                placeholder="Type"
                                value={x.acType ?? ''}
                                onChange={(e) => editCustomValue(x.id, 'acType', (e.value as string).toUpperCase())}
                            />
                            <NumericTextBox
                                min={0}
                                placeholder="Speed"
                                value={x.speed}
                                format={'0 kts'}
                                onChange={(e) => editCustomValue(x.id, 'speed', e.value)}
                            />
                            <NumericTextBox
                                min={0}
                                placeholder="Fuel burn"
                                value={x.fuelBurningRate}
                                onChange={(e) => editCustomValue(x.id, 'fuelBurningRate', e.value)}
                            />
                            <NumericTextBox
                                min={0}
                                placeholder="Fuel reserve"
                                value={x.fuelReserve}
                                onChange={(e) => editCustomValue(x.id, 'fuelReserve', e.value)}
                            />
                            <Button svgIcon={TrashOutline} themeColor={'error'} fillMode={'outline'} onClick={() => removeCustom(x.id)} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
